import { onDocumentReady } from '../utils/events';

/**
 * Cookie-consent manager (Quebec Law 25 / GDPR-ready).
 *
 * - First visit: shows the banner. Nothing non-essential runs until the visitor
 *   makes an affirmative choice ("Accept all" / "Refuse all" / "Customize").
 * - Stores the choice in the `crescita_consent` cookie for 6 months.
 * - Applies consent via Google Consent Mode v2 (gtag) AND by activating any
 *   `<script type="text/plain" data-cookie-category="...">` tag on the page.
 * - The footer "Manage cookies" link ([data-cookie-preferences]) reopens the modal.
 *
 * Public API: window.CrescitaConsent
 *   .get()                        -> current consent object
 *   .open()                       -> open the preferences modal
 *   .onConsent(category, cb)      -> run cb() now if granted, and on every change
 */

const COOKIE_NAME = 'crescita_consent';
const COOKIE_VERSION = 1;
const COOKIE_DAYS = 183; // ~6 months
const CATEGORIES = ['analytics', 'marketing'];

const readCookie = () => {
  try {
    const match = document.cookie.match(
      new RegExp(`(?:^|;\\s*)${COOKIE_NAME}=([^;]+)`)
    );
    if (!match) return null;
    const data = JSON.parse(decodeURIComponent(match[1]));
    if (!data || data.v !== COOKIE_VERSION) return null; // re-prompt on version bump
    return data;
  } catch (e) {
    return null;
  }
};

const writeCookie = (consent) => {
  const value = encodeURIComponent(
    JSON.stringify({
      v: COOKIE_VERSION,
      necessary: true,
      analytics: !!consent.analytics,
      marketing: !!consent.marketing,
      ts: Date.now(),
    })
  );
  const expires = new Date(
    Date.now() + COOKIE_DAYS * 24 * 60 * 60 * 1000
  ).toUTCString();
  const secure = window.location.protocol === 'https:' ? '; Secure' : '';
  document.cookie = `${COOKIE_NAME}=${value}; expires=${expires}; path=/; SameSite=Lax${secure}`;
};

const listeners = [];

const gtagUpdate = (consent) => {
  if (typeof window.gtag !== 'function') return;
  window.gtag('consent', 'update', {
    ad_storage: consent.marketing ? 'granted' : 'denied',
    ad_user_data: consent.marketing ? 'granted' : 'denied',
    ad_personalization: consent.marketing ? 'granted' : 'denied',
    analytics_storage: consent.analytics ? 'granted' : 'denied',
  });
};

// Activate scripts parked as <script type="text/plain" data-cookie-category="x">.
const activateScripts = (consent) => {
  document
    .querySelectorAll('script[type="text/plain"][data-cookie-category]')
    .forEach((node) => {
      const category = node.getAttribute('data-cookie-category');
      if (category !== 'necessary' && !consent[category]) return;

      const script = document.createElement('script');
      Array.from(node.attributes).forEach((attr) => {
        if (attr.name === 'type' || attr.name === 'data-cookie-category')
          return;
        script.setAttribute(attr.name, attr.value);
      });
      script.type = 'text/javascript';
      if (node.src) {
        script.src = node.src;
      } else {
        script.textContent = node.textContent;
      }
      node.parentNode.replaceChild(script, node);
    });
};

const applyConsent = (consent) => {
  gtagUpdate(consent);
  activateScripts(consent);
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ event: 'cookie_consent_update', consent });
  document.body.classList.toggle('has-analytics-consent', !!consent.analytics);
  document.body.classList.toggle('has-marketing-consent', !!consent.marketing);
  listeners.forEach((l) => {
    if (consent[l.category]) l.cb();
  });
};

onDocumentReady(() => {
  const root = document.querySelector('[data-cookie-consent]');
  if (!root) return;

  const banner = root.querySelector('[data-cookie-banner]');
  const modal = root.querySelector('[data-cookie-modal]');
  const toggles = Array.from(
    root.querySelectorAll('input[data-cookie-category]')
  );

  let lastFocus = null;

  const showBanner = (show) => {
    if (!banner) return;
    banner.hidden = !show;
    root.hidden = banner.hidden && (!modal || modal.hidden);
  };

  const openModal = () => {
    if (!modal) return;
    lastFocus = document.activeElement;
    // reflect the current stored state in the toggles
    const current = readCookie() || {};
    toggles.forEach((input) => {
      input.checked = !!current[input.dataset.cookieCategory];
    });
    root.hidden = false;
    modal.hidden = false;
    document.body.classList.add('cookie-consent-open');
    const focusTarget = modal.querySelector(
      'button, input, a[href], [tabindex]'
    );
    if (focusTarget) focusTarget.focus();
  };

  const closeModal = () => {
    if (!modal) return;
    modal.hidden = true;
    document.body.classList.remove('cookie-consent-open');
    root.hidden = !banner || banner.hidden;
    if (lastFocus && typeof lastFocus.focus === 'function') lastFocus.focus();
  };

  const commit = (consent) => {
    writeCookie(consent);
    applyConsent(consent);
    showBanner(false);
    closeModal();
  };

  const acceptAll = () => commit({ analytics: true, marketing: true });
  const rejectAll = () => commit({ analytics: false, marketing: false });
  const saveChoices = () => {
    const consent = {};
    CATEGORIES.forEach((cat) => {
      const input = toggles.find((t) => t.dataset.cookieCategory === cat);
      consent[cat] = input ? input.checked : false;
    });
    commit(consent);
  };

  root.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-cookie-action]');
    if (!trigger || !root.contains(trigger)) return;
    switch (trigger.dataset.cookieAction) {
      case 'accept':
        acceptAll();
        break;
      case 'reject':
        rejectAll();
        break;
      case 'customize':
        openModal();
        break;
      case 'save':
        saveChoices();
        break;
      case 'close':
        closeModal();
        break;
      default:
        break;
    }
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && modal && !modal.hidden) closeModal();
  });

  // Footer "Manage cookies" link (and any other opener).
  document.addEventListener('click', (event) => {
    const opener = event.target.closest('[data-cookie-preferences]');
    if (!opener) return;
    event.preventDefault();
    openModal();
  });

  // Public API for wiring bespoke tags from other scripts.
  window.CrescitaConsent = {
    get: () => readCookie(),
    open: openModal,
    onConsent: (category, cb) => {
      listeners.push({ category, cb });
      const current = readCookie();
      if (current && current[category]) cb();
    },
  };

  const stored = readCookie();
  if (stored) {
    applyConsent(stored); // returning visitor — honour their choice, stay quiet
    showBanner(false);
  } else {
    showBanner(true); // first visit — ask
  }
});
